import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Target, Globe, Mail, ChevronRight, ChevronLeft, Rocket, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SLIDES = [
  {
    title: "Welcome to LeadsB2B!",
    subtitle: "Let's set up your pipeline in 60 seconds",
    description: "LeadsB2B helps you find decision-makers, enrich lead data, and send high-converting email sequences on autopilot.",
    icon: Rocket,
    color: "text-[#002FA7] bg-[#002FA7]/10",
  },
  {
    title: "Step 1: Create a Campaign",
    subtitle: "Organize your outreach target list",
    description: "Define a campaign based on your niche, industry, location, and the role titles of your target buyers.",
    icon: Target,
    color: "text-[#FF5722] bg-[#FF5722]/10",
  },
  {
    title: "Step 2: Source Prospects",
    subtitle: "Domain scraper, CSVs, and lookalikes",
    description: "Add prospects using the Domain Scraper (AI), CSV Upload & Enrich, or Lookalike Search to clone your best leads.",
    icon: Globe,
    color: "text-[#002FA7] bg-[#002FA7]/10",
  },
  {
    title: "Step 3: Launch Drip Sequences",
    subtitle: "Personalized outreach on autopilot",
    description: "Write your email copy, schedule follow-ups, and send via your SMTP or the built-in Brevo integration.",
    icon: Mail,
    color: "text-[#00B0FF] bg-[#00B0FF]/10",
  }
];

export default function OnboardingModal() {
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [completing, setCompleting] = useState(false);

  // Guard: only show for users who haven't completed onboarding
  if (!user || user.has_completed_onboarding) return null;

  const Icon = SLIDES[step].icon;
  const slide = SLIDES[step];

  const handleNext = async () => {
    if (step < SLIDES.length - 1) {
      setStep(step + 1);
    } else {
      setCompleting(true);
      try {
        await api.post("/users/onboarding-complete");
        await refresh();
        toast.success("You're all set! Happy prospecting.");
      } catch (err) {
        toast.error("Failed to save onboarding progress. Please try again.");
      } finally {
        setCompleting(false);
      }
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent className="max-w-md p-6 bg-white border border-[#EAEAE3] rounded-2xl shadow-soft-lg text-left [&>button]:hidden">
        <div className="space-y-5">
          {/* Header */}
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${slide.color}`}>
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#9A9AA3]">
                Setup Guide — {step + 1} of {SLIDES.length}
              </div>
              <h2 className="font-display text-xl font-bold text-[#0A0A0F] mt-1">
                {slide.title}
              </h2>
              <p className="text-xs text-[#5C5C66] mt-0.5">{slide.subtitle}</p>
            </div>
          </div>

          <p className="text-sm text-[#5C5C66] leading-relaxed">{slide.description}</p>

          {/* Inline welcome for step 0 */}
          {step === 0 && user && (
            <div className="bg-[#FAFAF7] border border-[#EAEAE3] rounded-xl p-4 text-sm text-[#5C5C66]">
              👋 Hey <strong>{user.name}</strong>, this quick walkthrough shows you the core workflow so you get the most out of your {user.leads_quota || 50} free prospect credits.
            </div>
          )}
          {step === 1 && (
            <div className="bg-[#FAFAF7] border border-[#EAEAE3] rounded-xl p-3 flex gap-3 items-center text-sm text-[#5C5C66]">
              <span className="w-5 h-5 rounded-full bg-[#EAEAE3] flex items-center justify-center font-mono text-xs font-bold text-[#0A0A0F] shrink-0">1</span>
              <span>Go to <strong>Campaigns</strong> and click <strong>New Campaign</strong> to define your ICP.</span>
            </div>
          )}
          {step === 2 && (
            <ul className="space-y-1.5 list-disc pl-4 text-sm text-[#5C5C66]">
              <li><strong>Domain Scraper:</strong> Enter a company domain, AI finds decision-makers.</li>
              <li><strong>Enrich CSV:</strong> Upload emails and fill missing details automatically.</li>
              <li><strong>Lookalike Search:</strong> Clone your best existing leads.</li>
            </ul>
          )}
          {step === 3 && (
            <div className="bg-[#FAFAF7] border border-[#EAEAE3] rounded-xl p-3 flex gap-2 items-center text-sm text-[#5C5C66]">
              <Sparkles className="h-4 w-4 text-[#002FA7] shrink-0" />
              <span>Check your <strong>Domain Health</strong> score in Settings before launching your first campaign!</span>
            </div>
          )}

          {/* Progress & Navigation */}
          <div className="flex items-center justify-between pt-4 border-t border-[#EAEAE3]">
            <div className="flex gap-1.5">
              {SLIDES.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    idx === step ? "w-6 bg-[#0A0A0F]" : "w-1.5 bg-[#EAEAE3]"
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              {step > 0 && (
                <Button
                  variant="ghost"
                  onClick={() => setStep(step - 1)}
                  disabled={completing}
                  className="rounded-lg h-9 text-xs px-3 hover:bg-[#F4F4EE]"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
              )}
              <Button
                onClick={handleNext}
                disabled={completing}
                className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white h-9 text-xs font-medium px-4"
              >
                {completing ? "Saving…" : step === SLIDES.length - 1 ? "Get Started" : "Continue"}
                {!completing && step < SLIDES.length - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
